if (!customElements.get('collection-tabs-sort')) {
  customElements.define(
    'collection-tabs-sort',
    class CollectionTabsSort extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = 0;
        this.currentSort = 'default';
        this.onTabClick = this.onTabClick.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onSortToggleClick = this.onSortToggleClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
      }

      connectedCallback() {
        if (this._abortController) {
          this._abortController.abort();
        }

        this._abortController = new AbortController();
        const { signal } = this._abortController;

        this.currentSort = this.dataset.defaultSort || 'default';
        this.tabButtons = this.querySelectorAll('[data-tab-index]');
        this.panels = this.querySelectorAll('.collection-tabs-sort__panel');
        this.resultsCountEl = this.querySelector('[data-results-count]');
        this.sortFilter = this.querySelector('.sort-by-filter');

        for (const tabButton of this.tabButtons) {
          tabButton.addEventListener('click', this.onTabClick, { signal });
        }

        if (this.sortFilter) {
          this.sortFilter.addEventListener('click', this.onSortToggleClick, { signal });
          this.sortFilter.addEventListener('change', this.onSortChange, { signal });
          document.addEventListener('click', this.onDocumentClick, { signal });
          this.updateSelectedLabel();
        }

        if (this.tabButtons.length > 0) {
          this.activateTab(0);
        }
      }

      disconnectedCallback() {
        if (this._abortController) {
          this._abortController.abort();
        }
      }

      onTabClick(event) {
        event.preventDefault();
        const tabButton = event.currentTarget;
        const index = Number.parseInt(tabButton.dataset.tabIndex, 10);

        if (Number.isNaN(index)) {
          return;
        }

        this.activateTab(index);
      }

      onSortToggleClick(event) {
        const toggle = event.target.closest('.sort-by-filter__toggle');

        if (!toggle || !this.sortFilter?.contains(toggle)) {
          return;
        }

        event.stopPropagation();
        this.toggleSortDropdown();
      }

      onSortChange(event) {
        const input = event.target;

        if (!input.matches('.sort-by-filter__option-input')) {
          return;
        }

        if (!input.checked) {
          input.checked = true;
          return;
        }

        for (const optionInput of this.sortFilter.querySelectorAll('.sort-by-filter__option-input')) {
          optionInput.checked = optionInput === input;
        }

        this.updateSelectedLabel();
        this.currentSort = input.value;
        this.applySort(this.currentSort);
        this.closeSortDropdown();
      }

      onDocumentClick(event) {
        if (this.sortFilter && !this.sortFilter.contains(event.target)) {
          this.closeSortDropdown();
        }
      }

      activateTab(index) {
        if (!this.panels[index]) {
          return;
        }

        this.activeIndex = index;

        for (const tabButton of this.tabButtons) {
          const tabIndex = Number.parseInt(tabButton.dataset.tabIndex, 10);
          const isActive = tabIndex === index;
          tabButton.classList.toggle('is-active', isActive);
          tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }

        for (const panel of this.panels) {
          const panelIndex = Number.parseInt(panel.dataset.panelIndex, 10);
          const isActive = panelIndex === index;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        }

        this.scrollActiveTabIntoView(this.tabButtons[index]);
        this.updateCount();
        this.applySort(this.currentSort);
      }

      scrollActiveTabIntoView(activeTab) {
        if (!activeTab) {
          return;
        }

        const navRow = this.querySelector('.collection-tabs-sort__nav-row');

        if (!navRow) {
          return;
        }

        const navRect = navRow.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const isFullyVisible = tabRect.left >= navRect.left && tabRect.right <= navRect.right;

        if (isFullyVisible) {
          return;
        }

        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }

      getActiveSortGrid() {
        const activePanel = this.panels[this.activeIndex];

        if (!activePanel) {
          return null;
        }

        return activePanel.querySelector('[data-sort-grid]');
      }

      applySort(sortValue) {
        if (sortValue === 'default') {
          return;
        }

        const grid = this.getActiveSortGrid();

        if (!grid) {
          return;
        }

        const items = [...grid.querySelectorAll('.grid__item')];

        if (items.length === 0) {
          return;
        }

        const sortedItems = items.sort((firstItem, secondItem) => {
          const firstPrice = Number.parseInt(firstItem.dataset.price, 10) || 0;
          const secondPrice = Number.parseInt(secondItem.dataset.price, 10) || 0;

          if (sortValue === 'price-desc') {
            return secondPrice - firstPrice;
          }

          return firstPrice - secondPrice;
        });

        for (const item of sortedItems) {
          grid.appendChild(item);
        }
      }

      updateCount() {
        if (!this.resultsCountEl) {
          return;
        }

        const activePanel = this.panels[this.activeIndex];
        const count = activePanel ? activePanel.querySelectorAll('.grid__item').length : 0;
        const paddedCount = String(count).padStart(2, '0');

        this.resultsCountEl.textContent = `${paddedCount} ${count === 1 ? 'Result' : 'Results'}`;
      }

      updateSelectedLabel() {
        const labelSpan = this.sortFilter?.querySelector('.sort-by-filter__label');
        if (!labelSpan) return;
        const checkedInput = this.sortFilter.querySelector('.sort-by-filter__option-input:checked');
        labelSpan.textContent = checkedInput
          ? checkedInput.closest('label')?.querySelector('.sort-by-filter__option-label')?.textContent?.trim() ?? 'Sort by:'
          : 'Sort by:';
      }

      toggleSortDropdown() {
        if (this.sortFilter?.classList.contains('sort-by-filter--open')) {
          this.closeSortDropdown();
          return;
        }

        this.openSortDropdown();
      }

      openSortDropdown() {
        const dropdown = this.sortFilter?.querySelector('.sort-by-filter__dropdown');
        const toggle = this.sortFilter?.querySelector('.sort-by-filter__toggle');

        if (!dropdown || !toggle) {
          return;
        }

        this.sortFilter.classList.add('sort-by-filter--open');
        toggle.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
      }

      closeSortDropdown() {
        const dropdown = this.sortFilter?.querySelector('.sort-by-filter__dropdown');
        const toggle = this.sortFilter?.querySelector('.sort-by-filter__toggle');

        if (this.sortFilter) {
          this.sortFilter.classList.remove('sort-by-filter--open');
        }

        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        }

        if (dropdown) {
          dropdown.hidden = true;
        }
      }
    }
  );
}
